import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { trackEvent } from "../../modules/analytics";

const LeaseExtensionForm = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const lease = state?.lease;
  const yearsRemaining = state?.yearsRemaining;

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    userType: "Mortgage Broker",
    businessName: "",
    propertyType: "Flat",
    propertyValue: "",
    primeCentralLondon: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isHouse = form.propertyType === "House";

  const handleChange = (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [e.target.name]: value });

    if (e.target.name === "propertyType") {
      trackEvent("property_type_selected", { type: value });
    }
    if (e.target.name === "primeCentralLondon" && value === true) {
      trackEvent("pcl_selected");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    const endpoint = isHouse ? "/calculator/callback" : "/calculator/submit";
    const body = isHouse
      ? { contact: form, lease }
      : {
          contact: form,
          lease,
          propertyValue: form.propertyValue,
          yearsRemaining,
          locationCategory: form.primeCentralLondon ? "PCL" : "NW",
        };

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (!data.success) {
        setError("Something went wrong. Please try again.");
        return;
      }

      trackEvent("form_submitted", {
        property_type: form.propertyType,
        user_type: form.userType,
        pcl: form.primeCentralLondon,
      });

      if (isHouse) {
        navigate("/calculator/callback-sent");
      } else {
        navigate("/calculator/results", {
          state: { result: data.result, contact: form, lease, yearsRemaining },
        });
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!lease) {
    return (
      <div className="login-container mt-4">
        <p style={{ color: "red" }}>
          No lease selected. Please go back and select a property.
        </p>
        <button onClick={() => navigate("/")}>Back to Search</button>
      </div>
    );
  }

  return (
    <div style={{ width: 500, margin: "0 auto" }} className="mt-4">
      <h2 className="mb-3">Lease Extension Enquiry</h2>

      <table className="table table-bordered mb-4">
        <tbody>
          <tr>
            <th>Property</th>
            <td>{lease.dpa_single_address_label}</td>
          </tr>
          <tr>
            <th>Lease remaining</th>
            <td>{yearsRemaining} years</td>
          </tr>
        </tbody>
      </table>

      {isHouse && (
        <div
          className="mb-3 p-3"
          style={{
            border: "1px solid #76f0c5",
            borderRadius: 8,
            color: "#76f0c5",
          }}
        >
          A house requires a bespoke calculation. Enter your details and we will
          call you.
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label>Name</label>
          <input
            type="text"
            name="name"
            className="form-control"
            value={form.name}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label>Email</label>
          <input
            type="email"
            name="email"
            className="form-control"
            value={form.email}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label>Phone</label>
          <input
            type="tel"
            name="phone"
            className="form-control"
            value={form.phone}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="d-block mb-2">I am a</label>
          <div className="d-flex" style={{ gap: 12 }}>
            {["Mortgage Broker", "Leaseholder"].map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setForm({ ...form, userType: type })}
                style={{
                  flex: 1,
                  padding: "12px 8px",
                  borderRadius: 8,
                  border: `2px solid ${form.userType === type ? "#76f0c5" : "#444"}`,
                  background: form.userType === type ? "rgba(118,240,197,0.15)" : "transparent",
                  color: form.userType === type ? "#76f0c5" : "#aaa",
                  fontWeight: form.userType === type ? 600 : 400,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
        {form.userType === "Mortgage Broker" && (
          <div className="mb-3">
            <label>Business name</label>
            <input
              type="text"
              name="businessName"
              className="form-control"
              value={form.businessName}
              onChange={handleChange}
              required
            />
          </div>
        )}
        <div className="mb-3">
          <label>Property type</label>
          <select
            name="propertyType"
            className="form-control"
            value={form.propertyType}
            onChange={handleChange}
          >
            <option>Flat</option>
            <option>House</option>
          </select>
        </div>
        {!isHouse && (
          <>
            <div className="mb-3">
              <label>Estimated property value with long lease (£)</label>
              <input
                type="number"
                name="propertyValue"
                className="form-control"
                value={form.propertyValue}
                onChange={handleChange}
                min="1"
                required
              />
              {form.propertyValue && (
                <small style={{ color: "#76f0c5" }}>
                  £{Number(form.propertyValue).toLocaleString("en-GB")}
                </small>
              )}
            </div>
            <div className="mb-3 d-flex align-items-center" style={{ gap: 10 }}>
              <input
                type="checkbox"
                name="primeCentralLondon"
                id="primeCentralLondon"
                checked={form.primeCentralLondon}
                onChange={handleChange}
                style={{ width: "auto" }}
              />
              <label htmlFor="primeCentralLondon" style={{ marginBottom: 0 }}>
                Is the flat in Prime Central London?
              </label>
            </div>
          </>
        )}
        {error && <p style={{ color: "red" }}>{error}</p>}
        <p
          style={{
            color: "gray",
            fontSize: "0.85rem",
            marginBottom: "0.75rem",
          }}
        >
          By submitting this form you agree to be contacted by Lendle Broker
          Services regarding your lease extension enquiry.
        </p>
        <button type="submit" disabled={submitting}>
          {submitting
            ? "Submitting..."
            : isHouse
            ? "Request a Call Back"
            : "Calculate"}
        </button>
      </form>
    </div>
  );
};

export default LeaseExtensionForm;
